
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCcVisa, 
  faCcMastercard, 
  faCcAmex, 
  faCcJcb
} from '@fortawesome/free-brands-svg-icons';
import { 
  faTrash,
  faStar as faStarSolid
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CardManagement = () => {
  const [showForm, setShowForm] = useState(false);
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [errors, setErrors] = useState({});

  const user = localStorage.getItem('AficFreshtoken');

  // Fetch cards on component mount
  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await fetch(
        // 'http://localhost:3005/card'
        "https://afric-fresh-backend.onrender.com/card"
      , {
        headers: {
          'Authorization': `Bearer ${user}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCards(data.cards);
      } else {
        throw new Error('Failed to fetch cards');
      }
    } catch (error) {
      toast.error('Failed to load cards');
    } finally {
      setIsLoading(false);
    }
  };

  // Card validation functions
  const validateCardNumber = (number) => {
    const regex = /^[0-9]{16}$/;
    return regex.test(number.replace(/\s/g, ''));
  };

  const validateCardHolder = (name) => {
    const regex = /^[a-zA-Z\s]{2,50}$/;
    return regex.test(name);
  };

  const validateExpiry = (month, year) => {
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    
    month = parseInt(month);
    year = parseInt(year);

    if (year < currentYear || year > currentYear + 10) return false;
    if (month < 1 || month > 12) return false;
    if (year === currentYear && month < currentMonth) return false;
    
    return true;
  };

  const validateCVV = (cvv) => {
    const regex = /^[0-9]{3,4}$/;
    return regex.test(cvv);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      const formatted = value.replace(/\s/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim();
      setCardData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setCardData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        const response = await fetch(
          // `http://localhost:3005/cards/${cardId}`
          `https://afric-fresh-backend.onrender.com/cards/${cardId}`
          , {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user}`
          }
        });

        if (response.ok) {
          setCards(cards.filter(card => card.id !== cardId));
          toast.success('Card deleted successfully');
        } else {
          throw new Error('Failed to delete card');
        }
      } catch (error) {
        toast.error('Failed to delete card');
      }
    }
  };

  const handleSetDefaultCard = async (cardId) => {
    try {
      const response = await fetch(
        // `http://localhost:3005/card/${cardId}/default`
        `https://afric-fresh-backend.onrender.com/card/${cardId}/default`
        , {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user}`
        }
      });

      if (response.ok) {
        setCards(cards.map(card => ({
          ...card,
          isDefault: card.id === cardId
        })));
        toast.success('Default card updated');
      } else {
        throw new Error('Failed to update default card');
      }
    } catch (error) {
      toast.error('Failed to update default card');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate all fields
    if (!validateCardNumber(cardData.cardNumber)) {
      newErrors.cardNumber = 'Invalid card number';
    }
    if (!validateCardHolder(cardData.cardHolder)) {
      newErrors.cardHolder = 'Invalid card holder name';
    }
    if (!validateExpiry(cardData.expiryMonth, cardData.expiryYear)) {
      newErrors.expiry = 'Invalid expiry date';
    }
    if (!validateCVV(cardData.cvv)) {
      newErrors.cvv = 'Invalid CVV';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch(
        // 'http://localhost:3005/card'
        'https://afric-fresh-backend.onrender.com/card', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user}`
        },
        body: JSON.stringify(cardData)
      });

      if (response.ok) {
        const data = await response.json();
        setCards([...cards, data.card]);
        toast.success('Card added successfully');
        setShowForm(false);
        setCardData({
          cardNumber: '',
          cardHolder: '',
          expiryMonth: '',
          expiryYear: '',
          cvv: ''
        });
      } else {
        throw new Error('Failed to save card');
      }
    } catch (error) {
      toast.error('Failed to save card');
    }
  };

  const getCardIcon = (brand) => {
    switch (brand) {
      case 'visa':
        return <FontAwesomeIcon icon={faCcVisa} className="text-[#1A1F71] text-2xl" />;
      case 'mastercard':
        return <FontAwesomeIcon icon={faCcMastercard} className="text-[#EB001B] text-2xl" />;
      case 'amex':
        return <FontAwesomeIcon icon={faCcAmex} className="text-[#2E77BB] text-2xl" />;
      case 'jcb':
        return <FontAwesomeIcon icon={faCcJcb} className="text-[#0B4EA2] text-2xl" />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-sm:p-1 bg-gray-100 min-h-screen max-sm:mt-20">
      {/* Cards Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold mb-4">Your Cards</h2>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            {/* Existing Cards */}
            {cards.length > 0 && (
              <div className="mb-6 space-y-4">
                {cards.map(card => (
                  <div key={card.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      {getCardIcon(card.brand)}
                      <div>
                        <p className="font-medium">{card.cardHolder}</p>
                        <p className="text-gray-500">**** **** **** {card.last4}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleSetDefaultCard(card.id)}
                        className="text-gray-500 hover:text-yellow-500"
                        title={card.isDefault ? "Default card" : "Set as default"}
                      >
                        <FontAwesomeIcon 
                          icon={card.isDefault ? faStarSolid : faStarRegular}
                          className={card.isDefault ? "text-yellow-500" : ""}
                        />
                      </button>
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        className="text-gray-500 hover:text-red-500"
                        title="Delete card"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Card Button or Form */}
            {!showForm ? (
              <div className="border border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center bg-gray-50 h-40">
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center gap-2 text-[#007D44] font-semibold hover:underline"
                >
                  <span className="text-2xl">+</span> Add new card
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Card Number</label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={cardData.cardNumber}
                    onChange={handleInputChange}
                    maxLength="19"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="1234 5678 9012 3456"
                  />
                  {errors.cardNumber && <p className="text-red-500 text-sm">{errors.cardNumber}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Card Holder Name</label>
                  <input
                    type="text"
                    name="cardHolder"
                    value={cardData.cardHolder}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="John Doe"
                  />
                  {errors.cardHolder && <p className="text-red-500 text-sm">{errors.cardHolder}</p>}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Month</label>
                    <select
                      name="expiryMonth"
                      value={cardData.expiryMonth}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">MM</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                        <option key={month} value={month.toString().padStart(2, '0')}>
                          {month.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Year</label>
                    <select
                      name="expiryYear"
                      value={cardData.expiryYear}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">YY</option>
                      {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() % 100 + i).map(year => (
                        <option key={year} value={year.toString().padStart(2, '0')}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">CVV</label>
                    <input
                      type="password"
                      name="cvv"
                      value={cardData.cvv}
                      onChange={handleInputChange}
                      maxLength="4"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="123"
                    />
                    {errors.cvv && <p className="text-red-500 text-sm">{errors.cvv}</p>}
                  </div>
                </div>

                {errors.expiry && <p className="text-red-500 text-sm">{errors.expiry}</p>}

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-[#007D44] text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Save Card
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>

      {/* Security Information Section */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-lg font-bold mb-4">Your payment information is secure</h2>
        <ul className="list-none space-y-2">
          <li className="flex items-center gap-2 text-[#007D44]">
            <span>&#10003;</span>
            <p>We follow the Payment Card Industry Data Security Standard (PCI DSS)</p>
          </li>
          <li className="flex items-center gap-2 text-[#007D44]">
            <span>&#10003;</span>
            <p>All information remains secure and uncompromised</p>
          </li>
          <li className="flex items-center gap-2 text-[#007D44]">
            <span>&#10003;</span>
            <p>All data is encrypted</p>
          </li>
          <li className="flex items-center gap-2 text-[#007D44]">
            <span>&#10003;</span>
            <p>Your card information will never be mishandled or sold</p>
          </li>
        </ul>

        <div className="flex items-center gap-6 mt-6">
          <FontAwesomeIcon icon={faCcVisa} className="text-[#1A1F71] h-8" title="Visa Secure" />
          <FontAwesomeIcon icon={faCcMastercard} className="text-[#EB001B] h-8" title="Mastercard ID Check" />
          <FontAwesomeIcon icon={faCcAmex} className="text-[#2E77BB] h-8" title="American Express" />
          <FontAwesomeIcon icon={faCcJcb} className="text-[#0B4EA2] h-8" title="JCB Secure" />
        </div>
      </div>
    </div>
  );
};

export default CardManagement;